import * as angular from 'angular';
import * as _ from 'lodash';

import * as Actions from './groups.constants';

import { IGroupService } from '../../services/';
import { Dispatch, GetStore, Dispatcher } from '../Store';
import { Group, CreateGroupFromGroupDto, GroupFromTags, IRow } from '../../models';
import { loadGroups, loadGroupsSuccess, loadIdentifiersSuccess } from '../actions';
import { duplicateGroup, duplicateGroupError, loadGroup, loadGroupSuccess, addGroup, addGroupError, addGroupSuccess, updateGroup, updateGroupError, updateGroupSuccess } from './groups.actions';
import { IAlarmActionsService } from '../alarms/alarms.actionCreator';
import { IValueViewModelActionsService } from '../valueViewModels/valueViewModels.actionCreator';
import { ITagDefinitionActionsService } from '../../stateManagement/tags/tags.actionCreator';
import { loadIdentifiers } from '../identifiers/identifiers.actions';

declare type StrDic = { [name: string]: string; };

export interface IGroupActionsService {
    getOne(guid: string, force?: boolean): Dispatcher<Group>;
    getAll(): Dispatcher<Group[]>;
    getAll(force: boolean): Dispatcher<Group[]>;
    create(group: Group): Dispatcher<Group>;
    update(group: Group): Dispatcher<Group>;
    duplicate(data: CreateGroupFromGroupDto): Dispatcher<Group[]>;
    delete(groupGuid: string): Dispatcher<Group>;
    updateAllOccurenceMatrix(): Dispatcher<StrDic>;
    updateSomeOccurenceMatrix(guids: string[]): Dispatcher<StrDic>;
    forceInMemoryUpdate(group: Group): Dispatcher<Group>;
    createOrUpdateWithTags(groupFromTags: GroupFromTags): Dispatcher<Group>;
}

export class GroupActionsService implements IGroupActionsService {
    static serviceId = 'groupActionsService';

    static $inject = [
        '$q',
        'groupService',
        'alarmActionsService',
        'valueViewModelActionsService',
        'tagDefinitionActionsService'
    ];
    constructor(private _$q: angular.IQService,
        private _groupService: IGroupService,
        private _alarmActionsService: IAlarmActionsService,
        private _valueViewModelActionsService: IValueViewModelActionsService,
        private _tagsActionService: ITagDefinitionActionsService) {
    }

    create(group: Group): Dispatcher<Group> {
        return (dispatch: Dispatch<Group>, getStore: GetStore) => {
            const { groups } = getStore();
            const existingGuids = Object.values(groups.dictionary).map(g => g.guid);
            dispatch(addGroup());
            return this._groupService
                .addGroup(group)
                .then(data => {
                    const createdGroups = data
                        .map(g => g.group)
                        .filter(g => existingGuids.indexOf(g.guid) < 0);
                    const createdGroup = createdGroups.length ? createdGroups[0] : null;
                    dispatch(addGroupSuccess(createdGroup));
                    return createdGroup;
                })
                .catch(reason => {
                    dispatch(addGroupError());
                    throw reason;
                });
        };
    }

    update(group: Group): Dispatcher<Group> {
        return (dispatch: Dispatch<Group>, getStore: GetStore) => {
            dispatch(updateGroup());
            return this._groupService
                .updateGroup(group)
                .then(data => {
                    const updatedGroup = data
                        .map(g => g.group)
                        .filter(g => g.guid === group.guid)[0];
                    dispatch(updateGroupSuccess(updatedGroup));
                    return updatedGroup;
                })
                .catch(reason => {
                    dispatch(updateGroupError());
                    throw reason;
                });
        };
    }

    getOne(guid: string, force?: boolean): Dispatcher<Group> {
        return (dispatch: Dispatch<Group>, getStore: GetStore) => {
            const { groups } = getStore();
            if (force || (!groups.isLoading && !groups.isLoaded)) {
                dispatch(loadGroup());
                return this._groupService
                    .getAll(true)
                    .then(data => {
                        const groups = data.map(g => g.group).filter(g => g.guid === guid);
                        dispatch(loadGroupSuccess(groups[0]) as any);
                        const found = _.find(groups, g => g.guid === guid);
                        return found;
                    })
                    .catch(reason => {
                        dispatch({ type: Actions.GROUP_LOAD_ERROR, payload: reason });
                        throw reason;
                    });
            } else {
                const found = _.find(_.values<Group>(groups.dictionary), g => g.guid === guid);
                return this._$q.when(found);
            }
        };
    }

    getAll();
    getAll(force: boolean);
    getAll(force?: boolean): Dispatcher<Group[]> {
        return (dispatch: Dispatch<any>, getStore: GetStore) => {
            const { groups } = getStore();
            if (force || (!groups.isLoading && !groups.isLoaded)) {
                dispatch(loadGroups());
                dispatch(loadIdentifiers());
                //load alarm preemptively
                //Could load each identifier's alarms after loading alarms (maybe one day?)
                this._alarmActionsService.getAll()(dispatch as any, getStore);
                return this._groupService
                    .getAll(force)
                    .then(data => {
                        const groups = data.map(d => d.group);
                        const identifiers = [].concat(...groups.map(g => g.identifierCollection));
                        dispatch(loadGroupsSuccess(groups));
                        dispatch(loadIdentifiersSuccess(identifiers) as any);
                        return groups;
                    })
                    .catch(reason => {
                        dispatch({ type: Actions.GROUPS_LOAD_ERROR, payload: reason });
                        throw reason;
                    });
            } else {
                return this._$q.when(_.values<Group>(groups.dictionary));
            }
        };
    }

    duplicate(data: CreateGroupFromGroupDto): Dispatcher<Group[]> {
        return (dispatch: Dispatch<Group[]>, getStore: GetStore) => {
            dispatch(duplicateGroup());
            return this._groupService
                .createFrom(data)
                .then(data => {
                    // Refresh subsequent elements
                    return this._$q.all([
                        this._valueViewModelActionsService.getAll(true)(dispatch, getStore),
                        this._tagsActionService.getAll(true)(dispatch as any, getStore),
                        this._alarmActionsService.getAll(true)(dispatch as any, getStore)
                    ]);
                })
                .then(data => {
                    // cannot known if duplicate has created multiple groups
                    // force get all
                    return this.getAll(true)(dispatch, getStore);
                })
                .catch(reason => {
                    dispatch(duplicateGroupError(reason));
                    throw reason;
                });
        };
    }

    delete(groupGuid: string): Dispatcher<Group> {
        return (dispatch: Dispatch<Group>, getStore: GetStore) => {
            const { groups } = getStore();
            const group = groups.dictionary[groupGuid];
            dispatch({ type: Actions.GROUP_DELETE });
            return this._groupService
                .deleteGroup(groupGuid)
                .then(data => {
                    dispatch({ type: Actions.GROUP_DELETE_SUCCESS, payload: group });
                    this._valueViewModelActionsService.getAll(true)(dispatch, getStore);
                    this._tagsActionService.getAll(true)(dispatch as any, getStore);
                    return group;
                })
                .catch(reason => {
                    dispatch({ type: Actions.GROUP_DELETE_ERROR, payload: reason });
                    throw reason;
                });
        };
    }

    updateAllOccurenceMatrix(): Dispatcher<StrDic> {
        return (dispatch: Dispatch<StrDic>, getStore: GetStore) => {
            let result: { [name: string]: string; } = {};
            return this._groupService
                .updateAllOccurenceMatrix()
                .then(data => {
                    result = data;
                    this._valueViewModelActionsService.getAll(true)(dispatch, getStore);
                    return this.getAll(true)(dispatch, getStore);
                })
                .then(data => {
                    return result;
                })
                .catch(reason => {
                    throw reason;
                });
        };
    }

    updateSomeOccurenceMatrix(guids: string[]): Dispatcher<StrDic> {
        return (dispatch: Dispatch<StrDic>, getStore: GetStore) => {
            let result: { [name: string]: string; } = {};
            return this._groupService
                .updateSomeOccurenceMatrix(guids)
                .then(data => {
                    result = data;
                    this._valueViewModelActionsService.getAll(true)(dispatch, getStore);
                    return this.getAll(true)(dispatch, getStore);
                })
                .then(data => {
                    return result;
                })
                .catch(reason => {
                    throw reason;
                });
        };
    }

    forceInMemoryUpdate(group: Group): Dispatcher<Group> {
        return (dispatch: Dispatch<Group>, getStore: GetStore) => {
            dispatch({ type: Actions.GROUP_UPDATE_SUCCESS, payload: group });
            return this._valueViewModelActionsService
                .getAll(true)(dispatch, getStore)
                .then(() => this._$q.resolve(group));
        };
    }

    createOrUpdateWithTags(groupFromTags: GroupFromTags): Dispatcher<Group> {
        return (dispatch: Dispatch<Group>, getStore: GetStore) => {
            dispatch(updateGroup());
            return this._groupService
                .createGroupFromTags(groupFromTags)
                .then(data => {
                    const { group } = data;
                    dispatch({ type: Actions.GROUP_UPDATE_SUCCESS, payload: group });
                    return this._$q.all([
                        this._valueViewModelActionsService.getAll(true)(dispatch, getStore),
                        group,
                        this._tagsActionService.getAll(true)(dispatch as any, getStore)]);
                })
                .then(data => {
                    return data[1];
                });
        };
    }
}